<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import ClipLoader from '@/components/ui/ClipLoader.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconDownload from '@/components/icon/IconDownload.vue'
import { languageBadge, isUnreachableStatus, windowOf, formatCountdown, formatDuration, durationLabel, formatClock, joinLanguageNames, FALLBACK_CATALOG, WAITING_FOR_STREAM_HELP } from './captionLanguages'
import { formatSessionStart, sessionExportLangs, sessionLengthSec } from './captionExport'
import { DEFAULT_LIMITS, DEFAULT_USAGE, hasPool, extendPresetsFor, formatHours, hoursRunOutLabel } from './captionLimits'
import { translationWarning } from './captionTranslations'

/**
 * The session card under the preview player: per-language badges (Captions
 * off / Starting / Waiting for stream / Live / Stalled / Session ended /
 * Stopped, see `languageBadge`), the window countdown, soft translation
 * warnings from `status.production.translation`, and with `controls` the
 * running window's Extend and Stop. While the captioning service is
 * unreachable every badge reads "Unknown".
 */
const props = defineProps({
	flow: { type: Object, required: true },
	status: { type: Object, default: null },
	catalog: { type: Object, default: () => FALLBACK_CATALOG },
	// Server clock minus client clock at the last poll; badges and the countdown
	// use `Date.now() + skew` so a wrong client clock cannot flip a state.
	skewMs: { type: Number, default: 0 },
	// Seconds left (server-adjusted, display only): the window's, or the plan's
	// hours when those run out first; null when unlimited/not running.
	remainingSec: { type: Number, default: null },
	// The plan's hours end before the window: the chip counts them instead.
	poolEndsFirst: { type: Boolean, default: false },
	// Error message from the last failed status fetch, if any.
	error: { type: String, default: '' },
	// Core could not reach the captions service (banner is rendered by the parent).
	unreachable: { type: Boolean, default: false },
	// Server time of the last restart (settings save, Apply now): Starting grace counts from it.
	restartedAt: { type: Number, default: null },
	// Show the running window's Window / Extend / Stop controls.
	controls: { type: Boolean, default: false },
	// Plan limits and live usage (captionLimits.js), for the Extend presets.
	limits: { type: Object, default: () => ({ ...DEFAULT_LIMITS }) },
	usage: { type: Object, default: () => ({ ...DEFAULT_USAGE }) },
	extending: { type: Boolean, default: false },
	stopping: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	// The newest ended session (`normalizeSession` shape), shown while captions are off.
	lastSession: { type: Object, default: null },
	// Core has the export endpoints: show "Download captions" (opens the modal).
	downloadable: { type: Boolean, default: false },
})

const emit = defineEmits(['extend', 'stop', 'download'])

const now = ref(Date.now())
let tick = null
onMounted(() => { tick = setInterval(() => { now.value = Date.now() }, 1000) })
onBeforeUnmount(() => clearInterval(tick))

const statusUsable = computed(() => !!props.status && !isUnreachableStatus(props.status))
const unknown = computed(() => props.unreachable || (!!props.status && isUnreachableStatus(props.status)))
const configured = computed(() => (statusUsable.value ? !!props.status.configured : !!props.flow.configured))
const serverNow = computed(() => now.value + props.skewMs)
const win = computed(() => windowOf(statusUsable.value ? props.status : null, props.flow))
const running = computed(() => win.value.state === 'running')

const pills = computed(() => {
	const codes = statusUsable.value && Array.isArray(props.status.config?.langs) && props.status.config.langs.length
		? props.status.config.langs
		: (props.flow.langs || [])
	return codes.map((lang) => ({
		lang,
		...languageBadge(lang, statusUsable.value ? props.status : null, serverNow.value, {
			configured: configured.value,
			unknown: unknown.value,
			catalog: props.catalog,
			flow: props.flow,
			restartedAt: props.restartedAt,
		}),
		warning: running.value && statusUsable.value ? translationWarning(lang, props.status) : null,
	}))
})

const waitingStream = computed(() => running.value && pills.value.some((p) => p.key === 'waiting-stream'))
const warnings = computed(() => pills.value.filter((p) => p.warning).map((p) => ({ lang: p.lang, name: p.name, ...p.warning })))
const starting = computed(() => running.value && pills.value.some((p) => p.key === 'starting') && !pills.value.some((p) => p.key === 'live'))
const off = computed(() => configured.value && !unknown.value && !running.value)
const stalledNames = computed(() => pills.value.filter((p) => p.key === 'stalled').map((p) => p.name).join(', '))

const lastSessionLabel = computed(() => {
	const s = props.lastSession
	if (!off.value || !s) return ''
	const langs = joinLanguageNames(sessionExportLangs(s), props.catalog)
	return [formatSessionStart(s.startedAt), formatDuration(sessionLengthSec(s, serverNow.value)), langs].filter(Boolean).join(' · ')
})

const countdownLabel = computed(() => {
	if (!running.value) return ''
	if (props.poolEndsFirst && Number.isFinite(props.remainingSec)) {
		return props.remainingSec <= 0 ? 'Hours used up' : hoursRunOutLabel(props.remainingSec)
	}
	if (win.value.durationSec === null || win.value.durationSec === undefined) return 'Unlimited'
	if (!Number.isFinite(props.remainingSec)) return ''
	return props.remainingSec <= 0 ? 'Ended' : `Ends in ${formatCountdown(props.remainingSec)}`
})

// ------------------------------------------------------- Running controls
const showControls = computed(() => props.controls && running.value)
const unlimited = computed(() => win.value.durationSec === null || win.value.durationSec === undefined)
const planActive = computed(() => props.limits?.enforced === true)
const poolRemaining = computed(() => (hasPool(props.limits) && Number.isFinite(props.usage?.remainingSec) ? props.usage.remainingSec : null))
const cycleLeftLabel = computed(() => (poolRemaining.value === null ? '' : `${formatHours(poolRemaining.value)} left this cycle`))
const endsAtLabel = computed(() => (Number.isFinite(win.value.expiresAt) ? formatClock(win.value.expiresAt) : ''))
// Presets that still fit in the pool and under the window cap.
const extendPresets = computed(() => extendPresetsFor(props.limits, poolRemaining.value, win.value.durationSec))
const extendBlockedNote = computed(() => {
	if (!planActive.value || extendPresets.value.length) return ''
	if (poolRemaining.value !== null && poolRemaining.value < 900) return 'Under 15 min of plan hours left.'
	if (Number.isFinite(props.limits?.maxWindowSec)) return `At plan max (${formatHours(props.limits.maxWindowSec)}).`
	return ''
})

// One text node so the label reads "English · Off · stopped" in every consumer.
const pillText = (pill) => `${pill.name} · ${pill.label}${pill.detail ? ` · ${pill.detail}` : ''}`
const pillTooltip = (pill) => [pill.tooltip, pill.warning?.text].filter(Boolean).join(' ')

const pillClass = (pill) => {
	switch (pill.tone) {
		case 'success':
			return 'bg-success-transparent text-success ring-success/30'
		case 'warning':
			return 'bg-surface-3 text-gold ring-gold/30'
		default:
			return 'bg-surface-3 text-surface-8 ring-surface-4'
	}
}

const dotClass = (pill) => {
	switch (pill.tone) {
		case 'success':
			return 'bg-success'
		case 'warning':
			return 'bg-gold'
		default:
			return 'bg-surface-6'
	}
}
</script>

<template>
	<section class="flex flex-col gap-2 rounded-xl bg-surface-2 px-4 py-3" aria-live="polite" aria-label="Caption status" data-testid="caption-session">
		<div class="flex flex-wrap items-center gap-2">
			<template v-if="pills.length">
				<Tooltip v-for="pill in pills" :key="pill.lang" :active="!!pillTooltip(pill)" :content="pillTooltip(pill)" wrapper-class="inline-flex">
					<span
						class="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-tiny font-medium ring-1"
						:class="pillClass(pill)"
						:data-lang="pill.lang"
						:data-state="pill.key"
						:data-warning="pill.warning?.key"
					>
						<ClipLoader v-if="pill.spinner" size="10px" class="shrink-0" />
						<span v-else class="size-1.5 shrink-0 rounded-full" :class="[dotClass(pill), pill.key === 'live' ? 'animate-pulse' : '']" aria-hidden="true" />
						<span>{{ pillText(pill) }}</span>
						<span v-if="pill.warning" class="flex size-4 items-center justify-center rounded-full bg-gold/20 text-[10px] font-bold text-gold" aria-hidden="true">!</span>
					</span>
				</Tooltip>
			</template>
			<span v-else class="inline-flex h-7 items-center gap-1.5 rounded-full bg-surface-3 px-2.5 text-tiny font-medium text-surface-8 ring-1 ring-surface-4">
				<span class="size-1.5 rounded-full bg-surface-6" aria-hidden="true" />
				Not configured
			</span>

			<span
				v-if="countdownLabel"
				class="ml-auto inline-flex h-7 items-center rounded-full bg-surface-3 px-2.5 text-tiny font-medium tabular-nums ring-1"
				:class="poolEndsFirst ? 'text-gold ring-gold/30' : 'text-surface-text-2 ring-surface-4'"
				aria-live="off"
				data-testid="window-countdown"
			>{{ countdownLabel }}</span>
		</div>

		<p v-if="error" class="m-0 text-xs text-error-light" role="alert">{{ error }}</p>
		<p v-else-if="unknown && configured" class="m-0 text-xs text-surface-8">
			Caption status is unavailable right now. Captions may still be running.
		</p>
		<p v-else-if="waitingStream" class="m-0 text-xs text-surface-8" data-testid="waiting-stream-copy">
			{{ WAITING_FOR_STREAM_HELP }}
		</p>
		<p v-else-if="starting" class="m-0 text-xs text-surface-8" data-testid="starting-copy">
			Captions typically appear within a minute.
		</p>
		<p v-else-if="off" class="m-0 text-xs text-surface-8" data-testid="off-copy">
			<template v-if="win.endedReason === 'pool_exhausted'">Captions stopped because your plan's captioning hours for this cycle are used up. Upgrade or wait for the next cycle to caption again.</template>
			<template v-else>
				<template v-if="win.state === 'expired'">The session ended: its time limit was reached. </template>
				<template v-else-if="win.state === 'stopped'">Captions were stopped. </template>
				Start a new window to caption again.
			</template>
		</p>
		<p v-else-if="!configured" class="m-0 text-xs text-surface-8">
			Choose a source language, the languages to publish and a duration, then start.
		</p>
		<p v-else-if="stalledNames" class="m-0 text-xs text-surface-8">
			Captions in {{ stalledNames }} are falling behind. They usually catch up on their own; if not, stop and start captions again.
		</p>
		<ul v-if="warnings.length" class="m-0 flex list-none flex-col gap-1 p-0" data-testid="translation-warnings">
			<li
				v-for="w in warnings"
				:key="w.lang"
				class="flex items-start gap-1.5 text-xs text-gold"
				:data-warning-lang="w.lang"
				:data-warning="w.key"
				data-testid="translation-warning"
			>
				<span class="mt-px flex size-3.5 shrink-0 items-center justify-center rounded-full bg-gold/20 text-[9px] font-bold" aria-hidden="true">!</span>
				<span>{{ w.name }}: {{ w.text }}</span>
			</li>
		</ul>

		<!-- Running window: what you touch while live. -->
		<div v-if="showControls" class="mt-1 flex flex-col gap-2 border-t border-surface-3 pt-3" aria-live="off" data-testid="caption-window">
			<div class="flex flex-wrap items-center justify-between gap-2">
				<span class="text-tiny font-medium text-surface-text-1">
					Window <span class="text-surface-7">·</span> <span data-testid="window-duration">{{ durationLabel(win.durationSec) }}</span>
					<template v-if="!poolEndsFirst && !unlimited && endsAtLabel">
						<span class="text-surface-7"> · </span><span class="font-normal text-surface-8" data-testid="window-ends-at">ends {{ endsAtLabel }}</span>
					</template>
					<span v-else-if="!poolEndsFirst && unlimited" class="font-normal text-surface-8"> · runs until you stop it</span>
				</span>
				<div class="flex items-center gap-2">
					<FormButton v-if="downloadable" type="secondary" size="sm" data-testid="session-download" @click="emit('download', '')">
						<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="mr-1.5 text-surface-7"><icon-download /></icon-base>
						Download captions
					</FormButton>
					<FormButton
						type="red"
						size="sm"
						class="justify-center"
						:disabled="disabled || stopping"
						:is-loading="stopping"
						data-testid="stop-button"
						@click="emit('stop')"
					>
						Stop captions
					</FormButton>
				</div>
			</div>
			<p v-if="poolEndsFirst" class="m-0 text-xs text-gold">Your plan's hours run out before this window ends; captions stop then.</p>
			<div v-if="!unlimited" class="flex flex-wrap items-center gap-1.5" data-testid="caption-extend">
				<span class="mr-1 text-xs text-surface-7">Extend</span>
				<FormButton
					v-for="preset in extendPresets"
					:key="preset.value"
					type="secondary"
					size="sm"
					:disabled="disabled || extending || stopping"
					:is-loading="extending"
					@click="emit('extend', preset.value)"
				>
					{{ preset.label }}
				</FormButton>
				<span v-if="extendBlockedNote" class="text-xs text-surface-7" data-testid="extend-blocked">{{ extendBlockedNote }}</span>
				<span v-if="cycleLeftLabel" class="ml-auto text-[11px] tabular-nums text-surface-7" data-testid="cycle-left">{{ cycleLeftLabel }}</span>
			</div>
			<p v-else-if="cycleLeftLabel" class="m-0 text-[11px] tabular-nums text-surface-7" data-testid="cycle-left">{{ cycleLeftLabel }}</p>
		</div>

		<div v-if="downloadable && !showControls" class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-surface-3 pt-3" aria-live="off" data-testid="session-footer">
			<span v-if="lastSessionLabel" class="min-w-0 text-xs text-surface-8" data-testid="last-session">
				<span class="font-medium text-surface-text-1">Last session</span> · <span data-testid="last-session-summary">{{ lastSessionLabel }}</span>
			</span>
			<FormButton type="secondary" size="sm" class="ml-auto" data-testid="session-download" @click="emit('download', lastSessionLabel ? lastSession.id : '')">
				<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16" class="mr-1.5 text-surface-7"><icon-download /></icon-base>
				Download captions
			</FormButton>
		</div>
	</section>
</template>
